// src/components/walkingTour/TourThemesCard.tsx
//
// Renders a [TOUR_THEMES] block as tappable theme cards. Used during
// the awaiting_focus state of a walking tour, when the AI offers
// 3-4 theme options and the user has to pick one to drive
// generation.
//
// On tap, fires onSelect(themeName) which the parent wires to the
// existing follow-up-click pipeline — i.e. it sends a synthetic user
// message ("Let's go with Medieval Nürnberg") that the AI then
// responds to. The AI's response includes the CONVERSATION_STATE
// marker that flips the tour into the 'generating' state and kicks
// off the generator.
//
// Why route through a synthetic user message rather than directly
// emitting a CONVERSATION_STATE marker on tap:
//   1. Keeps the conversation legible — the user's choice is
//      visible in the chat history, just like if they'd typed it.
//   2. Lets the AI handle ambiguity. If the user picks "Beer focused
//      tour" but they're somewhere with no breweries, the AI can
//      adjust or push back rather than the frontend silently
//      committing to a doomed generation.
//   3. Reuses existing plumbing — onFollowUpClick already does the
//      "send synthetic user message" dance correctly.

import React from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Stack,
} from '@mui/material';
import {
  Route as RouteIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useT } from '../../i18n/useT';
import type { TourThemeItem } from '../../utils/interactiveContent';

interface Props {
  items: TourThemeItem[];
  /** Fired when the user taps a theme. The parent should treat this
   *  like a typed user message — send it to the AI and let the
   *  state machine play out. */
  onSelect: (themeName: string) => void;
}

const formatDistance = (km: number | undefined): string | null => {
  if (km === undefined) return null;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
};

const formatDuration = (min: number | undefined): string | null => {
  if (min === undefined) return null;
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  return rem === 0 ? `${hr} hr` : `${hr} hr ${rem} min`;
};

export const TourThemesCard: React.FC<Props> = ({ items, onSelect }) => {
  const t = useT();

  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Section heading — small uppercase chip lets the user
          recognize this as a structured choice surface, not just
          another paragraph of text. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.5,
          mb: 0.25,
        }}
      >
        <RouteIcon
          sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.7 }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            opacity: 0.7,
          }}
        >
          {t('Pick a theme')}
        </Typography>
      </Box>

      {items.map((theme, idx) => (
        <ThemeRow
          key={`${theme.name}-${idx}`}
          theme={theme}
          onSelect={() => onSelect(theme.name)}
        />
      ))}
    </Box>
  );
};

interface ThemeRowProps {
  theme: TourThemeItem;
  onSelect: () => void;
}

const ThemeRow: React.FC<ThemeRowProps> = ({ theme, onSelect }) => {
  const distance = formatDistance(theme.distance_km);
  const duration = formatDuration(theme.duration_min);

  return (
    <ButtonBase
      onClick={onSelect}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        textAlign: 'left',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 1.25,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        overflow: 'hidden',
        cursor: 'pointer',
        transition:
          'border-color 0.15s ease, background-color 0.15s ease, transform 0.05s ease',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
        },
        '&:active': {
          transform: 'scale(0.995)',
        },
      }}
    >
      {/* Optional glyph column. If the AI didn't provide one, render
          a small route badge so the row still has visual anchor. */}
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: theme.glyph ? '1.05rem' : '0.85rem',
          fontWeight: 700,
          mt: 0.1,
        }}
      >
        {theme.glyph || <RouteIcon sx={{ fontSize: 18 }} />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
        >
          {theme.name}
        </Typography>

        {theme.description && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.25,
              fontSize: '0.78rem',
              color: 'text.secondary',
              lineHeight: 1.35,
              wordBreak: 'break-word',
            }}
          >
            {theme.description}
          </Typography>
        )}

        {(distance || duration) && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}
          >
            {duration && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  color: 'text.secondary',
                }}
              >
                <ScheduleIcon sx={{ fontSize: 12 }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.72rem', fontWeight: 500 }}
                >
                  {duration}
                </Typography>
              </Box>
            )}
            {distance && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  color: 'text.secondary',
                }}
              >
                <RouteIcon sx={{ fontSize: 12 }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.72rem', fontWeight: 500 }}
                >
                  {distance}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </ButtonBase>
  );
};
